import React, { PropsWithChildren, ReactNode } from "react";
import { StaticImage } from "gatsby-plugin-image";
import { Link, type PageProps } from "gatsby";
import classNames from "classnames";
import { DecoratedSeparator } from "./DecoratedSeparator";
import { HighlightedText } from "./HighlightedText";

export default function Footer({
  backgroundClassName,
}: {
  backgroundClassName?: string;
}) {
  return (
    <footer role={"contentinfo"} className={"relative grid w-full"}>
      <figure
        className={classNames("relative w-full grid overflow-hidden pb-8", {
          "min-h-96": true,
        })}
      >
        <div className={"absolute inset-0 -z-10"}>
          <StaticImage
            src={"../images/teaserImg.jpg"}
            alt={"Blumen"}
            layout={"fullWidth"}
            quality={80}
            placeholder={"blurred"}
            objectFit={"cover"}
            objectPosition={"center"}
            className={"h-full w-full"}
            transformOptions={{
              fit: "cover",
              cropFocus: "attention",
            }}
          />
        </div>
        <div
          className={"grid relative place-items-center grid-rows-[auto,1fr]"}
          style={{
            // By using the same grid area for both, they are stacked on top of each other
            gridArea: "1/1",
          }}
        >
          <div className={"transform -scale-y-100 -translate-y-[1px] w-full"}>
            <DecoratedSeparator backgroundClassName={backgroundClassName} />
          </div>
          <div className={"grid items-center justify-center my-auto"}>
            <span
              className={
                "text-white text-center uppercase flex flex-col gap-4 justify-center items-center"
              }
            >
              <HighlightedText>
                freier Träger
                <br className={"lg:hidden"} /> für zeitgemäße
              </HighlightedText>
              <strong
                className={
                  "text-2xl lg:text-6xl text-shadow-sm lg:text-shadow-lg shadow-black"
                }
              >
                Kinderbetreuung
              </strong>
            </span>
          </div>
        </div>
      </figure>
      <div
        className={"grid gap-4 lg:flex lg:justify-between px-8 py-4 bg-grey"}
      >
        <span className={"uppercase font-bold"}>Fokus Familie gGmbH</span>
        <nav className={"flex flex-col lg:flex-row gap-4"}>
          <Link to={"/aktuelles/"}>Aktuelles</Link>
          <Link to={"/kontakt/"}>Kontakt</Link>
          <Link to={"/impressum/"}>Impressum</Link>
          <Link to={"/datenschutzerklaerung/"}>Datenschutzerklärung</Link>
        </nav>
      </div>
      <div className={"h-[250px]"}>
        <GMapEmbed src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2489.9645089484698!2d6.665349977451473!3d51.39429097178767!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b8bbeed14edbbd%3A0x6acc8eb16c3146b!2sAm%20Volkesberg%2030%2C%2047239%20Duisburg!5e0!3m2!1sde!2sde!4v1721907157626!5m2!1sde!2sde" />
      </div>
      <div className={"grid lg:grid-cols-2 gap-8 p-8 text-center"}>
        <Place
          title={
            <>
              KiTa <br className={"lg:hidden"} />
              Die&nbsp;Grünschnäbel
            </>
          }
          address={<>Länglingsweg&nbsp;66, 47447&nbsp;Moers</>}
          mapSrc={
            "https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d9951.089041310446!2d6.65975!3d51.42561!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0xb105321dc9ff7f4b!2zRGllIEdyw7xuc2NobsOkYmVs!5e0!3m2!1sde!2sde!4v1595593232354!5m2!1sde!2sde2sde"
          }
        />
        <Place
          title={
            <>
              KiTa <br className={"lg:hidden"} />
              Die&nbsp;4&nbsp;Elemente
            </>
          }
          address={<>Marie-Curie-Str.&nbsp;8, 47475&nbsp;Kamp-Lintfort</>}
          mapSrc={
            "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2484.572366011056!2d6.535106015924367!3d51.48436332020805!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47b8a2668a90807f%3A0x19ed4d26ebee477b!2sDie%204%20Elemente!5e0!3m2!1sde!2sde!4v1595593255715!5m2!1sde!2sde"
          }
        />
      </div>
    </footer>
  );
}

function Place({
  title,
  address,
  mapSrc,
}: {
  title: ReactNode;
  address: ReactNode;
  mapSrc: string;
}) {
  return (
    <div className={"bg-primary text-white p-8"}>
      <h3 className={"uppercase font-bold lg:text-2xl"}>{title}</h3>
      <address className={"not-italic"}>{address}</address>
      <div className={"h-[340px] mt-8"}>
        <GMapEmbed src={mapSrc} />
      </div>
    </div>
  );
}

function GMapEmbed({ src }: { src: string }) {
  return (
    <iframe
      src={src}
      width="100%"
      height="100%"
      className={"border-0 w-full h-full"}
      allowFullScreen
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
    ></iframe>
  );
}
